import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { MarkdownEditor } from "~/common/components/ui";
import { ConsultationInputLayout } from "~/providers/components";
import { billingNoteGenerationStatusMessages } from "~/providers/constants/billingNoteGenerationStatus";
import {
  useCompletePatientBillingNote,
  useUpdatePatientBillingNote,
} from "~/providers/hooks";
import {
  getReimbursementReportValues,
  reimbursementReportSchema,
  ReimbursementReportSchemaType,
} from "~/providers/utils";
import { PatientBillingNote } from "~/providers/utils/types/patientBillingNoteTypes";
import { SessionBillingNoteCompleteSaveButtons } from "./SessionBillingNoteCompleteSaveButtons";
import { SessionBillingNoteConfirmationModal } from "./SessionBillingNoteConfirmationModal";

interface SessionBillingNoteFormProps {
  patientId: string;
  sessionId: string;
  data: PatientBillingNote;
  generationFailed?: boolean;
}

export const SessionBillingNoteForm = ({
  patientId,
  sessionId,
  data,
  generationFailed = false,
}: SessionBillingNoteFormProps) => {
  const [saveChangesAutomatically, setSaveChangesAutomatically] =
    useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const toggleConfirmationModal = () =>
    setShowConfirmationModal((prev) => !prev);

  const { updatePatientBillingNoteMutation, isPending: pendingUpdateData } =
    useUpdatePatientBillingNote({
      patientId,
      sessionId,
      showSuccessMessage: !saveChangesAutomatically,
    });

  const { completePatientBillingNoteMutation, isPending: pendingComplete } =
    useCompletePatientBillingNote(patientId, sessionId);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    setValue,
  } = useForm<ReimbursementReportSchemaType>({
    resolver: zodResolver(reimbursementReportSchema),
    values: {
      saveData: true,
      data: {
        ...getReimbursementReportValues(data),
      },
    },
  });

  const onSubmit: SubmitHandler<ReimbursementReportSchemaType> = (formData) => {
    if (formData.saveData) {
      setSaveChangesAutomatically(false);
      updatePatientBillingNoteMutation(formData.data);
    } else {
      toggleConfirmationModal();
    }
  };

  const handleSubmitBillingNoteCompletion = () => {
    const billingNoteData = getValues("data");
    completePatientBillingNoteMutation(billingNoteData);
  };

  useEffect(() => {
    return () => {
      if (!isDirty || !saveChangesAutomatically) return;
      const formValues = getValues();
      formValues.saveData && updatePatientBillingNoteMutation(formValues.data);
    };
  }, [
    getValues,
    isDirty,
    saveChangesAutomatically,
    updatePatientBillingNoteMutation,
  ]);

  return (
    <div className="flex size-full flex-col gap-4">
      {generationFailed ? (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">
          {billingNoteGenerationStatusMessages.FAILED}
        </div>
      ) : null}
      <form
        className="flex size-full flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <ConsultationInputLayout label="Billing note">
          <Controller
            control={control}
            name="data.final_note"
            render={({ field }) => (
              <MarkdownEditor
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.data?.final_note?.message}
              />
            )}
          />
        </ConsultationInputLayout>
        <SessionBillingNoteCompleteSaveButtons
          isFormDisabled={false}
          pendingUpdateData={pendingUpdateData}
          setValue={setValue}
          isDirty={isDirty}
        />
      </form>
      <SessionBillingNoteConfirmationModal
        isPending={pendingComplete}
        show={showConfirmationModal}
        onClose={toggleConfirmationModal}
        onSubmit={handleSubmitBillingNoteCompletion}
      />
    </div>
  );
};
